import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { authApi, BACKSTAGE_TOKEN_KEY, type AuthUser } from './authApi';

type AuthContextValue = {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  signup: (username: string, password: string, displayName?: string) => Promise<void>;
  logout: () => void;
  updateProfile: (displayName: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function fetchGuestBackstageToken(): Promise<string | null> {
  try {
    const response = await fetch('/api/auth/guest/refresh');

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data?.backstageIdentity?.token ?? null;
  } catch {
    return null;
  }
}

async function saveGuestBackstageToken() {
  const guestToken = await fetchGuestBackstageToken();

  if (guestToken) {
    localStorage.setItem(BACKSTAGE_TOKEN_KEY, guestToken);
  }

  return guestToken;
}

export function AuthProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const establishSession = useCallback(async (nextToken: string, nextUser: AuthUser) => {
    authApi.setToken(nextToken);

    try {
      const backstageToken = await authApi.getBackstageToken(nextToken);
      localStorage.setItem(BACKSTAGE_TOKEN_KEY, backstageToken);
    } catch {
      await saveGuestBackstageToken();
    }

    setToken(nextToken);
    setUser(nextUser);
  }, []);

  useEffect(() => {
    let mounted = true;

    async function bootstrap() {
      try {
        const existingToken = authApi.getToken();

        if (!existingToken) {
          await saveGuestBackstageToken();

          if (mounted) {
            setToken(null);
            setUser(null);
            setLoading(false);
          }

          return;
        }

        const exp = Number(localStorage.getItem('local-auth-exp'));

        if (exp && Date.now() / 1000 > exp) {
          authApi.clearToken();
          localStorage.removeItem(BACKSTAGE_TOKEN_KEY);

          await saveGuestBackstageToken();

          if (mounted) {
            setToken(null);
            setUser(null);
            setLoading(false);
          }

          return;
        }

        const currentUser = await authApi.getCurrentUser(existingToken);

        try {
          const backstageToken = await authApi.getBackstageToken(existingToken);
          localStorage.setItem(BACKSTAGE_TOKEN_KEY, backstageToken);
        } catch {
          await saveGuestBackstageToken();
        }

        if (mounted) {
          setToken(existingToken);
          setUser(currentUser);
        }
      } catch (_error) {
        authApi.clearToken();
        localStorage.removeItem(BACKSTAGE_TOKEN_KEY);

        await saveGuestBackstageToken();

        if (mounted) {
          setToken(null);
          setUser(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    bootstrap();

    return () => {
      mounted = false;
    };
  }, []);

  const login = useCallback(
    async (username: string, password: string) => {
      const response = await authApi.login(username, password);
      await establishSession(response.token, response.user);
    },
    [establishSession],
  );

  const signup = useCallback(
    async (username: string, password: string, displayName?: string) => {
      const response = await authApi.signup(username, password, displayName);
      await establishSession(response.token, response.user);
    },
    [establishSession],
  );

  const logout = useCallback(() => {
    authApi.clearToken();
    localStorage.removeItem(BACKSTAGE_TOKEN_KEY);
    window.location.href = '/';
  }, []);

  const updateProfile = useCallback(
    async (displayName: string) => {
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await authApi.updateProfile(token, displayName);
      authApi.setToken(response.token);

      try {
        const backstageToken = await authApi.getBackstageToken(response.token);
        localStorage.setItem(BACKSTAGE_TOKEN_KEY, backstageToken);
      } catch {
        // Keep the existing session if the bridge token refresh fails.
      }

      setToken(response.token);
      setUser(response.user);
    },
    [token],
  );

  useEffect(() => {
    const isAdmin = (user?.roles ?? []).includes('admin');
    document.body.classList.toggle('admin-user', isAdmin);

    return () => {
      document.body.classList.remove('admin-user');
    };
  }, [user]);

  const value = useMemo(
    () => ({ user, token, loading, login, signup, logout, updateProfile }),
    [loading, login, signup, logout, updateProfile, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
}